/**
 * This file contains the UI for managing a course's students, and is
 * integrated into the new payments system.
 */

import { urls } from './urls';
import { useRouteParams } from '@components/router';
import { ComponentChildren } from 'preact';
import { GuideCoursePage, GuideProductPage } from '@course/components/guide-course-page';
import { IcoDownload } from '@components/icons';
import { BtnSecondary, Button } from '@components/buttons';
import { useMemo, useState } from 'preact/hooks';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';
import { last } from 'shared/utils';
import { serialAsync } from 'client/utils/serial-async';
import { showError } from '@components/app-error';
import { FilterBox, FilterOptions } from '@course/components/table/filter-box';
import { Dropdown, MenuItem } from '@components/dropdown';
import { PageTitle } from '@course/components/headings';
import { StudentDetailSlideOver } from './student-detail-slideover';
import { store, PageType, FilterOpts, Props, load } from './state';
import { PaymentStats, PaymentsTable } from './payments-table';
import { ProgressStats, ProgressTable } from './progress-table';
import { Case } from '@components/conditional';
import { GiftsTable } from './gifts-table';
import { URLS as SHARED_URLS } from 'shared/urls';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { BulkExpirationModal } from './bulk-expiration-modal';
import { CertGateTab } from './cert-gate-tab';
import { CheckinsTab } from './checkins-tab';
import { showToast } from '@components/toaster';
import { pluralize } from 'shared/formatting';
import { hasLevel } from 'shared/auth';
import { useCurrentUser } from '@components/router/session-context';

export const route = defCoursesRoute({
  load,
  Page,
  authLevel: 'guide',
  disableSubrouteScroll: true,
});

function PageLink({
  children,
  page,
  courseId,
  baseUrl,
}: {
  page: PageType;
  baseUrl: string;
  courseId: string;
  children: ComponentChildren;
}) {
  const { pageType } = useRouteParams();
  return (
    <Button
      class={`border-b-2 ${page === pageType ? 'border-indigo-600' : 'border-transparent'}`}
      href={`${baseUrl}${courseId}/${page}${location.search}`}
    >
      {children}
    </Button>
  );
}

function PageContent(props: Props) {
  const { userId, pageType } = props.params;
  const { state, setState } = props;
  const { course, opts, students, terminology } = state;
  const hasFullAccess = course.level === 'guide';
  // The outreach CSV carries set-password links (keys to accounts): admins only.
  const isAdmin = hasLevel(useCurrentUser(), 'admin');
  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const { searchTerm } = state.opts;
  const showProgress = !course.isBundle && !course.isProduct;
  const isCertGate = pageType === 'completion-gate';
  const isCheckins = pageType === 'checkins';
  const showRosterTools = pageType !== 'gifts' && !isCertGate && !isCheckins;
  const baseUrl = course.isProduct ? urls.productsBaseUrl : urls.coursesBaseUrl;
  const [showBulkExpirationModal, setShowBulkExpirationModal] = useState(false);

  const resetOpts = (fn: (o: FilterOpts) => FilterOpts) => {
    setState((x) => ({
      ...x,
      opts: { ...fn(x.opts), startingAfter: undefined },
    }));
  };

  const loadNextPage = useMemo(() => {
    return serialAsync(async (opts: FilterOpts) => {
      try {
        setLoadingCount((x) => x + 1);
        const batch = await store.getNextStudents(opts);
        if (!opts.startingAfter) {
          setState((x) => ({ ...x, students: batch }));
        } else {
          setState((x) => ({
            ...x,
            students: {
              data: [...x.students.data, ...batch.data],
              hasMore: batch.hasMore,
            },
          }));
        }
      } catch (err) {
        showError(err);
      } finally {
        setLoadingCount((x) => Math.max(0, x - 1));
      }
    });
  }, []);

  useDidUpdateEffect(() => loadNextPage(state.opts), [state.opts]);

  return (
    <section class="flex flex-col gap-2 w-full max-w-7xl mx-auto">
      {userId && (
        <StudentDetailSlideOver
          key={userId}
          course={course}
          userId={userId}
          showProgress={showProgress}
          baseUrl={baseUrl}
          onClose={() => {
            props.router.goto(
              urls.showStudents({
                ...props.params,
                courseId: course.id,
                userId: '',
                baseUrl,
              }),
            );
          }}
          onChange={(student) => {
            setState((s) => ({
              ...s,
              students: {
                ...students,
                data: students.data.map((x) => (x.id === student.id ? { ...x, ...student } : x)),
              },
            }));
          }}
        />
      )}
      <div class="flex flex-col gap-8 grow p-8 w-full max-w-7xl mx-auto">
        <header class="flex flex-col gap-4 relative overflow-hidden">
          <div class="flex gap-6 items-center">
            <PageTitle>{terminology}</PageTitle>
            <nav class="order-last flex w-full gap-8 text-sm font-semibold border-l pl-6">
              {hasFullAccess && (
                <PageLink
                  page={course.isProduct ? 'customers' : 'students'}
                  courseId={course.id}
                  baseUrl={baseUrl}
                >
                  Payments
                </PageLink>
              )}
              {showProgress && (
                <PageLink page="progress" courseId={course.id} baseUrl={baseUrl}>
                  Progress
                </PageLink>
              )}
              {hasFullAccess && (
                <PageLink page="gifts" courseId={course.id} baseUrl={baseUrl}>
                  Gifts
                </PageLink>
              )}
              {showProgress && (
                <PageLink page="completion-gate" courseId={course.id} baseUrl={baseUrl}>
                  Completion gate
                </PageLink>
              )}
              {showProgress && course.checkinsEnabled && (
                <PageLink page="checkins" courseId={course.id} baseUrl={baseUrl}>
                  Standups
                </PageLink>
              )}
            </nav>
          </div>
          <div class="flex flex-col md:grid grid-cols-5 flex-wrap border-b border-t">
            {pageType === 'students' && <PaymentStats {...props.state} />}
            {pageType === 'progress' && <ProgressStats {...props.state} />}
          </div>
        </header>

        <Case when={showRosterTools}>
          <div class="flex flex-col md:flex-row md:justify-between items-center">
            <FilterBox
              searchPlaceholder={`Search ${terminology}`}
              compact={false}
              hasFilters={!!searchTerm || opts.access !== 'all'}
              searchTerm={searchTerm}
              setSearchTerm={(searchTerm) => resetOpts((s) => ({ ...s, searchTerm }))}
              clearFilters={() =>
                setState((s) => ({ ...s, opts: { ...s.opts, searchTerm: '', access: 'all' } }))
              }
            >
              <FilterOptions
                currentValue={opts.access}
                options={[
                  { title: 'All', value: 'all' },
                  { title: 'Granted', value: 'granted' },
                  { title: 'Expired', value: 'expired' },
                  { title: 'Revoked', value: 'revoked' },
                ]}
                title="Access"
                setFilter={(access: typeof opts.access) => resetOpts((x) => ({ ...x, access }))}
              />
            </FilterBox>

            <div class="flex items-center mt-4 md:mt-0 gap-4">
              {hasFullAccess && (
                <BtnSecondary onClick={() => setShowBulkExpirationModal(true)}>
                  Bulk Set Expiration
                </BtnSecondary>
              )}

              <Dropdown
                class="hidden md:flex"
                hideDownIcon
                triggerClass="text-gray-600"
                renderMenu={() => (
                  <div class="flex flex-col p-2 pb-0">
                    <MenuItem
                      download="memberships.csv"
                      href={`/csv/courses/${course.id}/memberships.csv`}
                    >
                      Overview
                    </MenuItem>
                    {hasFullAccess && (
                      <MenuItem
                        download="payments.csv"
                        href={`/csv/courses/${course.id}/payments.csv`}
                      >
                        Payments
                      </MenuItem>
                    )}
                    {showProgress && (
                      <MenuItem
                        download="progress.csv"
                        href={`/csv/courses/${course.id}/progress.csv`}
                      >
                        Progress
                      </MenuItem>
                    )}
                    {showProgress && isAdmin && (
                      <MenuItem
                        download="outreach.csv"
                        href={`/csv/courses/${course.id}/outreach.csv`}
                      >
                        Outreach (all students)
                      </MenuItem>
                    )}
                    {showProgress && isAdmin && (
                      <MenuItem
                        download="outreach-never-opened.csv"
                        href={`/csv/courses/${course.id}/outreach.csv?never=1`}
                      >
                        Outreach (never opened the course)
                      </MenuItem>
                    )}
                    {showProgress && course.checkinsEnabled && (
                      <MenuItem
                        download="standups.csv"
                        href={`/csv/courses/${course.id}/standups.csv`}
                      >
                        Standups
                      </MenuItem>
                    )}
                  </div>
                )}
              >
                <IcoDownload class="w-4 h-4 mr-1" />
                Download CSV
              </Dropdown>
            </div>
          </div>
        </Case>

        {(pageType === 'students' || pageType === 'customers') && hasFullAccess && (
          <PaymentsTable {...props} />
        )}
        {pageType === 'progress' && <ProgressTable {...props} />}
        {pageType === 'gifts' && hasFullAccess && <GiftsTable course={course} />}
        {isCertGate && showProgress && <CertGateTab courseId={course.id} />}
        {isCheckins && showProgress && <CheckinsTab courseId={course.id} baseUrl={baseUrl} />}

        {students.hasMore && showRosterTools && (
          <footer>
            <BtnSecondary
              isLoading={isLoading}
              onClick={() => {
                setState((x) => ({
                  ...x,
                  opts: { ...x.opts, startingAfter: last(students.data)?.id },
                }));
              }}
            >
              Load more
            </BtnSecondary>
          </footer>
        )}

        {showBulkExpirationModal && (
          <BulkExpirationModal
            courseId={course.id}
            hide={() => setShowBulkExpirationModal(false)}
            onSuccess={(updatedCount) => {
              setShowBulkExpirationModal(false);
              showToast({
                type: 'ok',
                title: 'Bulk update successful',
                message: `Expiration date updated for ${updatedCount} ${pluralize(
                  'student',
                  updatedCount,
                )}`,
              });

              // Reload the student list to reflect the changes
              loadNextPage({
                ...opts,
                startingAfter: undefined,
              });
            }}
          />
        )}
      </div>
    </section>
  );
}

function Page(props: Props) {
  if (props.url.startsWith(urls.productsBaseUrl)) {
    return (
      <GuideProductPage page="customers" product={props.state.course}>
        <PageContent {...props} />
      </GuideProductPage>
    );
  }

  const { course } = props.state;

  return (
    <GuideCoursePage
      type="students"
      course={course}
      viewLink={SHARED_URLS.student.coursePage({
        course,
        page: course.hidePeople ? '' : 'people',
      })}
    >
      <PageContent {...props} />
    </GuideCoursePage>
  );
}
